<template>
	<form ref="form" @submit.prevent="onSubmit">
		<slot />
	</form>
</template>

<script lang="ts" setup>
	import { ref, computed, provide } from 'vue'

	export type FormProvider = {
		register: (input: FormInput) => void
	}

	export type FormInput = {
		validate: () => Promise<boolean>
		getValue: () => Record<string, string | number> | undefined
	}

	const form = ref()

	const props = defineProps({
		value: {
			type: Boolean,
			default: undefined,
		},
		error: {
			type: String,
			default: undefined,
		},
	})

	const emit = defineEmits(['input', 'submit'])

	const inputs = ref<FormInput[]>([])
	const isValid = ref<boolean | undefined>(undefined)

	const localValue = computed<boolean>({
		get: () => props.value ?? isValid.value ?? false,
		set: (isValidValue: boolean) => {
			if (props.value === undefined) {
				isValid.value = isValidValue
			} else {
				isValid.value = undefined
				emit('input', isValidValue)
			}
		},
	})

	const register = (input: FormInput) => {
		inputs.value.push(input)
	}

	const validate = async () => {
		let isValidForm = true
		for (const input of inputs.value) {
			const isCurrentValid = await input.validate()
			if (!isCurrentValid) {
				isValidForm = false
			}
		}
		return isValidForm
	}

	const getValues = () => {
		let response: Record<string, string | number> = {}
		inputs.value.forEach((input) => {
			response = { ...input.getValue(), ...response }
		})
		return response
	}

	const submit = () => {
		const event = new Event('submit', { cancelable: true })
		form.value.dispatchEvent(event)
	}

	const onSubmit = async () => {
		localValue.value = await validate()
		if (localValue.value && !props.error) {
			const form = getValues()
			emit('submit', form)
		}
	}

	provide('form', { register } as FormProvider)
	defineExpose({
		submit,
	})
</script>
